import DOMPurify from 'isomorphic-dompurify'
import { cn } from '#app/utils/misc.tsx'

// Matches anything that looks like an HTML element tag. Content without tags
// (legacy plain-text rows, AI-assistant output) is rendered as text instead.
const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i

// Only what the tiptap editor (StarterKit + Link) emits.
const ALLOWED_TAGS = [
	'p',
	'br',
	'strong',
	'b',
	'em',
	'i',
	's',
	'u',
	'h1',
	'h2',
	'h3',
	'h4',
	'ul',
	'ol',
	'li',
	'blockquote',
	'a',
	'code',
	'pre',
]
const ALLOWED_ATTR = ['href', 'target', 'rel']

// Minimal block styling for sanitized HTML. Tailwind's preflight zeroes all
// element margins and there is no typography plugin, so without these a
// multi-paragraph text would render as one unbroken block.
const richContentStyles = cn(
	'[&>*+*]:mt-4',
	'[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
	'[&_blockquote]:border-l-2 [&_blockquote]:pl-4 [&_blockquote]:italic',
	'[&_a]:underline',
	'[&_h1]:text-2xl [&_h1]:font-medium [&_h2]:text-xl [&_h2]:font-medium [&_h3]:text-lg [&_h3]:font-medium [&_h4]:font-medium',
)

/**
 * Renders user-authored content that may be plain text (legacy rows,
 * AI-assistant output) or HTML from the tiptap WysiwygEditor. HTML is
 * sanitized with DOMPurify before rendering.
 */
export function RichContent({
	html,
	className,
}: {
	html: string
	className?: string
}) {
	if (!HTML_TAG_PATTERN.test(html)) {
		return <div className={cn('whitespace-pre-line', className)}>{html}</div>
	}

	const sanitized = DOMPurify.sanitize(html, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		ALLOW_DATA_ATTR: false,
	})

	return (
		<div
			className={cn(richContentStyles, className)}
			dangerouslySetInnerHTML={{ __html: sanitized }}
		/>
	)
}
